/**
 * SamvadDashboard — Role-adaptive dashboard for संस्कृत युवा संवाद कार्यक्रम
 * Sprint 3: 4 role views — student, upadhyaya, acharya, admin.
 *
 * Role switching: stored in localStorage as 'samvad_demo_role'.
 * This is a dev/demo mechanism until proper auth is wired in Sprint 4.
 * Participant ID: stored in localStorage as 'samvad_participant_id' (set on registration).
 *
 * Domain rules:
 *   - Age is metadata only — never used for knowledge gating.
 *   - Guru/Raja decisions are never automated — platform records and routes.
 *   - Stage (adhikara) is derived from progress, never stored directly.
 */
import { useState, useEffect, useCallback } from 'react';
import '../styles/SamvadDashboard.css';
import SamvadMatchingCard from './SamvadMatchingCard';
import { useGlossary } from '../contexts/GlossaryContext';
import { useAuth } from '../contexts/AuthContext';
import LoginModal from './LoginModal';

interface Props {
  onNavigate: (path: string) => void;
}

type DashRole = 'student' | 'upadhyaya' | 'acharya' | 'admin';

const ROLE_META: Record<DashRole, { label: string; labelEn: string; icon: string }> = {
  student:   { label: 'शिष्य',       labelEn: 'Student',   icon: '📚' },
  upadhyaya: { label: 'उपाध्याय',    labelEn: 'Upadhyaya', icon: '🎓' },
  acharya:   { label: 'आचार्य',      labelEn: 'Acharya',   icon: '🏛️' },
  admin:     { label: 'व्यवस्थापक',  labelEn: 'Admin',     icon: '⚙️' },
};

const PROFICIENCY_LABELS: Record<number, { hi: string; en: string }> = {
  1: { hi: 'नौसिखिया',   en: 'Beginner'     },
  2: { hi: 'प्रारम्भिक', en: 'Elementary'   },
  3: { hi: 'मध्यम',      en: 'Intermediate' },
  4: { hi: 'उन्नत',      en: 'Advanced'     },
  5: { hi: 'प्रवाहशील',  en: 'Fluent'       },
};

const MILESTONES = [
  { hours: 10,  id: '10h',  labelHi: 'दशघण्टा',     labelEn: '10 h' },
  { hours: 25,  id: '25h',  labelHi: 'पञ्चविंशति',  labelEn: '25 h' },
  { hours: 50,  id: '50h',  labelHi: 'पञ्चाशत्',    labelEn: '50 h' },
  { hours: 100, id: '100h', labelHi: 'शतघण्टा',     labelEn: '100 h' },
];

// ── Shared types ──────────────────────────────────────────────────────────────

interface ProgressData {
  participant_id:       string;
  total_hours:          number;
  sessions_count:       number;
  books_covered:        string[];
  milestone_badges:     string[];
  next_milestone_hours: number | null;
  next_milestone_label: string | null;
}

interface SessionRecord {
  id:               string;
  book_id:          string;
  session_type:     'solo' | 'paired';
  date:             string;
  duration_minutes: number;
  hours:            number;
  reflection:       string;
  partner_id:       string | null;
  youtube_url:      string | null;
}

interface ParticipantRecord {
  id:               string;
  full_name:        string;
  city:             string;
  pincode:          string;
  proficiency:      number;
  archetype:        string | null;
  status:           string;
  registered_at:    string;
  is_minor:         boolean;
  total_hours:      number;
  sessions_count:   number;
  milestone_badges: string[];
}

interface AdminData {
  total:           number;
  total_hours_all: number;
  participants:    ParticipantRecord[];
}

interface SamvadBook {
  id:                 string;
  title_hi:           string;
  title_en:           string;
  author_id:          string;
  phase:              number;
  target_proficiency: number;
  archetype:          string;
  status:             string;
  review_notes:       string;
  created_at:         string;
}

interface SamvadEvent {
  title_hi:    string;
  title_en:    string;
  date:        string;
  location:    string;
  type:        string;
  description: string;
}

interface Nomination {
  id:             string;
  participant_id: string;
  nominated_by:   string;
  title:          string;
  notes:          string;
  status:         string;
  nominated_at:   string;
}

const BOOK_STATUS_LABELS: Record<string, { hi: string; cls: string }> = {
  draft:     { hi: 'मसौदा',       cls: 'svd-bs-draft'     },
  submitted: { hi: 'समीक्षाधीन',  cls: 'svd-bs-submitted' },
  approved:  { hi: 'प्रकाशित',    cls: 'svd-bs-approved'  },
  rejected:  { hi: 'अस्वीकृत',   cls: 'svd-bs-rejected'  },
};

// ── Events section (shared) ───────────────────────────────────────────────────
function EventsSection() {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };
  const [events, setEvents]   = useState<SamvadEvent[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch('/api/samvad/events')
      .then(r => r.json())
      .then(d => { setEvents(d.events ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  if (loading || events.length === 0) return null;

  return (
    <div className="svd-events-section">
      <h3 className="svd-section-title">{tl('samvad.dash.events.heading', '📅 आगामी कार्यक्रम · Upcoming Events')}</h3>
      <ul className="svd-events-list">
        {events.map((e, i) => (
          <li key={i} className="svd-event-row">
            <div className="svd-event-type">{e.type}</div>
            <div className="svd-event-info">
              <span className="svd-event-title">{e.title_hi}</span>
              <span className="svd-event-en">{e.title_en}</span>
              <span className="svd-event-meta">📅 {e.date} · 📍 {e.location}</span>
              <span className="svd-event-desc">{e.description}</span>
            </div>
          </li>
        ))}
      </ul>
    </div>
  );
}

// ── Upadhyaya Book Section ────────────────────────────────────────────────────

function UpadhyayaBookSection({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };
  const authorId = localStorage.getItem('samvad_participant_id') ?? '';
  const [books,   setBooks]   = useState<SamvadBook[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authorId) { setLoading(false); return; }
    fetch(`/api/samvad/books?author_id=${encodeURIComponent(authorId)}`)
      .then(r => r.json())
      .then(d => { setBooks(d.books ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  }, [authorId]);

  if (!authorId) return null;

  return (
    <div className="svd-section svd-books-section">
      <div className="svd-section-row">
        <h3 className="svd-section-title">{tl('samvad.dash.books.my_drafts', '📖 मेरी पुस्तकें · My Drafts')}</h3>
        <button className="svd-btn-sm-primary" onClick={() => onNavigate('/samvad/content-editor')}>
          {tl('samvad.dash.books.new_book', '＋ नई पुस्तक')}
        </button>
      </div>
      {loading ? (
        <p className="svd-loading">{tl('samvad.dash.loading', 'लोड हो रहा है…')}</p>
      ) : books.length === 0 ? (
        <p className="svd-muted">{tl('samvad.dash.books.empty', 'अभी कोई पुस्तक नहीं — "नई पुस्तक" बटन से शुरू करें।')}</p>
      ) : (
        <ul className="svd-book-list">
          {books.map(b => {
            const sl = BOOK_STATUS_LABELS[b.status] ?? { hi: b.status, cls: '' };
            return (
              <li key={b.id} className="svd-book-row">
                <span className="svd-book-title">{b.title_hi}</span>
                <span className="svd-book-meta-chips">
                  <span className="svd-chip">{tl('samvad.dash.phase', 'चरण')} {b.phase}</span>
                  <span className="svd-chip">{tl('samvad.dash.level', 'स्तर')} {b.target_proficiency}</span>
                </span>
                <span className={`svd-book-status ${sl.cls}`}>{sl.hi}</span>
                {b.review_notes && (
                  <span className="svd-book-notes" title={b.review_notes}>💬</span>
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

// ── Acharya Book Review Queue ─────────────────────────────────────────────────

function AcharyaBookQueue() {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };
  const reviewerId = localStorage.getItem('samvad_participant_id') ?? 'ACHARYA';
  const [books,    setBooks]    = useState<SamvadBook[]>([]);
  const [loading,  setLoading]  = useState(true);
  const [notes,    setNotes]    = useState<Record<string, string>>({});
  const [acting,   setActing]   = useState<string | null>(null);

  function loadBooks() {
    fetch('/api/samvad/books?status=submitted')
      .then(r => r.json())
      .then(d => { setBooks(d.books ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { loadBooks(); }, []);

  async function decide(bookId: string, status: 'approved' | 'rejected') {
    setActing(bookId);
    await fetch(`/api/samvad/books/${bookId}/status`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ reviewer_id: reviewerId, status, review_notes: notes[bookId] ?? '' }),
    }).catch(() => null);
    setActing(null);
    loadBooks();
  }

  return (
    <div className="svd-section svd-books-section">
      <h3 className="svd-section-title">{tl('samvad.dash.books.review_queue', '📚 पुस्तक समीक्षा कतार · Book Review Queue')}</h3>
      {loading ? (
        <p className="svd-loading">{tl('samvad.dash.loading', 'लोड हो रहा है…')}</p>
      ) : books.length === 0 ? (
        <p className="svd-muted">{tl('samvad.dash.books.review_empty', 'समीक्षा के लिए कोई पुस्तक नहीं।')}</p>
      ) : (
        <ul className="svd-book-list svd-book-list--review">
          {books.map(b => (
            <li key={b.id} className="svd-book-row svd-book-row--review">
              <div className="svd-book-review-info">
                <span className="svd-book-title">{b.title_hi}</span>
                {b.title_en && <span className="svd-book-en">{b.title_en}</span>}
                <span className="svd-book-meta-chips">
                  <span className="svd-chip">{tl('samvad.dash.phase', 'चरण')} {b.phase}</span>
                  <span className="svd-chip">{tl('samvad.dash.level', 'स्तर')} {b.target_proficiency}</span>
                  <span className="svd-chip">{b.archetype}</span>
                  <span className="svd-chip-id">{b.author_id}</span>
                </span>
              </div>
              <div className="svd-book-review-actions">
                <textarea
                  className="svd-review-notes-input"
                  rows={2}
                  placeholder={tl('samvad.dash.books.review_notes_placeholder', 'समीक्षा टिप्पणी…')}
                  value={notes[b.id] ?? ''}
                  onChange={e => setNotes(n => ({ ...n, [b.id]: e.target.value }))}
                />
                <div className="svd-review-btns">
                  <button
                    className="svd-btn-approve"
                    onClick={() => decide(b.id, 'approved')}
                    disabled={acting === b.id}
                  >{tl('samvad.dash.books.approve', '✅ प्रकाशित करें')}</button>
                  <button
                    className="svd-btn-reject"
                    onClick={() => decide(b.id, 'rejected')}
                    disabled={acting === b.id}
                  >{tl('samvad.dash.reject', '✗ अस्वीकार')}</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Acharya Certification Section ─────────────────────────────────────────────

function AcharyaCertSection() {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };
  const deciderId = localStorage.getItem('samvad_participant_id') ?? 'ACHARYA';
  const [noms,         setNoms]         = useState<Nomination[]>([]);
  const [loading,      setLoading]      = useState(true);
  const [decisionNotes, setDecisionNotes] = useState<Record<string, string>>({});
  const [nomPid,        setNomPid]        = useState('');
  const [nomTitle,      setNomTitle]      = useState('');
  const [nomNotes,      setNomNotes]      = useState('');
  const [nomError,      setNomError]      = useState('');
  const [submitting,    setSubmitting]    = useState(false);
  const [acting,        setActing]        = useState<string | null>(null);

  const TITLES = ['प्रवेश-शिष्य', 'बीज-शिष्य', 'अङ्कुर-शिष्य', 'पर्ण-शिष्य', 'पुष्प-शिष्य', 'फल-शिष्य'];

  function loadNoms() {
    fetch('/api/samvad/cert?status=nominated')
      .then(r => r.json())
      .then(d => { setNoms(d.nominations ?? []); setLoading(false); })
      .catch(() => setLoading(false));
  }

  useEffect(() => { loadNoms(); }, []);

  async function submitNomination() {
    if (!nomPid.trim() || !nomTitle || !nomNotes.trim()) {
      setNomError(tl('samvad.dash.cert.nom_error', 'प्रतिभागी ID, उपाधि, और टिप्पणी आवश्यक हैं।'));
      return;
    }
    setSubmitting(true);
    setNomError('');
    await fetch('/api/samvad/cert/nominate', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ participant_id: nomPid.trim(), nominated_by: deciderId, title: nomTitle, notes: nomNotes.trim() }),
    }).catch(() => null);
    setNomPid(''); setNomTitle(''); setNomNotes('');
    setSubmitting(false);
    loadNoms();
  }

  async function decide(nomId: string, action: 'approve' | 'decline') {
    const dn = (decisionNotes[nomId] ?? '').trim();
    if (!dn) { setDecisionNotes(n => ({ ...n, [nomId]: ' ' })); return; }
    setActing(nomId);
    await fetch(`/api/samvad/cert/${nomId}/decide`, {
      method:  'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ decided_by: deciderId, action, decision_notes: dn }),
    }).catch(() => null);
    setActing(null);
    loadNoms();
  }

  return (
    <div className="svd-section svd-cert-section">
      <h3 className="svd-section-title">{tl('samvad.dash.cert.heading', '🎖️ उपाधि-नामांकन · Certification Nominations')}</h3>
      <p className="svd-muted" style={{ marginBottom: '1rem', fontSize: '0.82rem' }}>
        {tl('samvad.dash.cert.domain_rule', 'Domain rule: Guru + Acharya joint decision required. Platform records only — never automates.')}
      </p>

      {/* Nominate form */}
      <div className="svd-nom-form">
        <h4 className="svd-nom-form-title">{tl('samvad.dash.cert.new_nom_heading', 'नया नामांकन · New Nomination')}</h4>
        <div className="svd-nom-row">
          <input
            className="svd-nom-input"
            placeholder={tl('samvad.dash.cert.participant_id_placeholder', 'प्रतिभागी ID (SV-…)')}
            value={nomPid}
            onChange={e => setNomPid(e.target.value)}
          />
          <select
            className="svd-nom-select"
            value={nomTitle}
            onChange={e => setNomTitle(e.target.value)}
          >
            <option value="">{tl('samvad.dash.cert.select_title', '— उपाधि चुनें —')}</option>
            {TITLES.map(title => <option key={title} value={title}>{title}</option>)}
          </select>
        </div>
        <textarea
          className="svd-nom-notes"
          rows={2}
          placeholder={tl('samvad.dash.cert.nom_notes_placeholder', 'नामांकन का कारण (required)…')}
          value={nomNotes}
          onChange={e => setNomNotes(e.target.value)}
        />
        {nomError && <p className="svd-nom-err">{nomError}</p>}
        <button className="svd-btn-sm-primary" onClick={submitNomination} disabled={submitting}>
          {submitting ? '⏳' : tl('samvad.dash.cert.nom_submit', '📨 नामांकन भेजें')}
        </button>
      </div>

      {/* Pending nominations */}
      {loading ? (
        <p className="svd-loading">{tl('samvad.dash.loading', 'लोड हो रहा है…')}</p>
      ) : noms.length === 0 ? (
        <p className="svd-muted">{tl('samvad.dash.cert.no_noms', 'कोई प्रतीक्षित नामांकन नहीं।')}</p>
      ) : (
        <ul className="svd-nom-list">
          {noms.map(n => (
            <li key={n.id} className="svd-nom-row">
              <div className="svd-nom-info">
                <span className="svd-nom-pid">{n.participant_id}</span>
                <span className="svd-nom-title-badge">{n.title}</span>
                <span className="svd-nom-notes-txt">{n.notes}</span>
              </div>
              <div className="svd-nom-actions">
                <textarea
                  className="svd-nom-dec-notes"
                  rows={1}
                  placeholder={tl('samvad.dash.cert.decision_notes_placeholder', 'निर्णय का कारण (required)')}
                  value={decisionNotes[n.id] ?? ''}
                  onChange={e => setDecisionNotes(prev => ({ ...prev, [n.id]: e.target.value }))}
                />
                <div className="svd-nom-btns">
                  <button className="svd-btn-approve" onClick={() => decide(n.id, 'approve')} disabled={acting === n.id}>{tl('samvad.dash.cert.approve', '✅ स्वीकृत')}</button>
                  <button className="svd-btn-reject"  onClick={() => decide(n.id, 'decline')} disabled={acting === n.id}>{tl('samvad.dash.reject', '✗ अस्वीकार')}</button>
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

// ── Student Panel ─────────────────────────────────────────────────────────────

function StudentPanel({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };
  const { user, authLoading } = useAuth();
  const [inputId, setInputId]     = useState(() => localStorage.getItem('samvad_participant_id') ?? '');
  const [activeId, setActiveId]   = useState(() => localStorage.getItem('samvad_participant_id') ?? '');
  const [progress, setProgress]   = useState<ProgressData | null>(null);
  const [sessions, setSessions]   = useState<SessionRecord[]>([]);
  const [loading, setLoading]     = useState(false);
  const [error, setError]         = useState('');
  // true once we've finished trying Firebase; prevents flash of ID input row
  const [firebaseResolved, setFirebaseResolved] = useState(false);

  const loadData = useCallback(async (id: string) => {
    const trimmed = id.trim();
    if (!trimmed) return;
    setLoading(true);
    setError('');
    try {
      const [pRes, sRes] = await Promise.all([
        fetch(`/api/samvad/progress/${trimmed}`),
        fetch(`/api/samvad/sessions/${trimmed}?limit=3`),
      ]);
      if (!pRes.ok) {
        const body = await pRes.json().catch(() => ({}));
        const msg = (body as { detail?: string; error?: string }).detail
          ?? (body as { error?: string }).error
          ?? 'प्रतिभागी ID नहीं मिली';
        if (pRes.status === 404) {
          // Stale or deleted participant ID — clear it so the user can re-register
          localStorage.removeItem('samvad_participant_id');
          setActiveId('');
          setInputId('');
        }
        throw new Error(msg);
      }
      const pData = await pRes.json() as ProgressData;
      const sData = sRes.ok ? await sRes.json() as { sessions: SessionRecord[] } : { sessions: [] };
      setProgress(pData);
      setSessions(sData.sessions ?? []);
      localStorage.setItem('samvad_participant_id', trimmed);
      setActiveId(trimmed);
    } catch (err) {
      setError(err instanceof Error ? err.message : tl('samvad.dash.network_error', 'नेटवर्क त्रुटि — पुनः प्रयास करें'));
      setProgress(null);
    } finally {
      setLoading(false);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Wait for Firebase auth to finish loading, then resolve participant ID
  useEffect(() => {
    if (authLoading) return; // wait — don't decide yet

    if (!user) {
      // Not signed in — fall back to localStorage
      setFirebaseResolved(true);
      if (activeId) loadData(activeId);
      return;
    }

    // Signed in — check if a samvad record is linked to this Firebase account
    fetch(`/api/samvad/me/${encodeURIComponent(user.uid)}`)
      .then(r => r.ok ? r.json() as Promise<{ participant_id: string }> : null)
      .then(data => {
        if (data?.participant_id) {
          localStorage.setItem('samvad_participant_id', data.participant_id);
          setInputId(data.participant_id);
          setActiveId(data.participant_id);
          loadData(data.participant_id);
        } else if (activeId) {
          // Firebase account exists but no samvad record — fall back to localStorage ID
          loadData(activeId);
        }
      })
      .catch(() => { if (activeId) loadData(activeId); })
      .finally(() => setFirebaseResolved(true));
  }, [authLoading, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const barPct = progress ? Math.min(100, (progress.total_hours / 100) * 100) : 0;

  return (
    <div className="svd-panel">

      {/* While auth + Firebase lookup is in progress, show a mini spinner */}
      {(authLoading || !firebaseResolved) && (
        <div className="svd-auth-resolving">
          <span className="svd-spin">⏳</span> {tl('samvad.dash.resolving', 'खाता जाँच रहे हैं…')}
        </div>
      )}

      {/* Participant ID input — shown only when not auto-resolved via Firebase */}
      {!authLoading && firebaseResolved && (!user || !activeId) && (
      <div className="svd-id-row">
        <label className="svd-id-label">{tl('samvad.dash.student.pid_label', 'प्रतिभागी ID · Participant ID')}</label>
        <div className="svd-id-input-group">
          <input
            className="svd-id-input"
            type="text"
            placeholder="SV-2025-00001"
            value={inputId}
            onChange={e => setInputId(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && loadData(inputId)}
          />
          <button
            className="svd-id-btn"
            onClick={() => loadData(inputId)}
            disabled={loading || !inputId.trim()}
          >
            {loading ? '…' : tl('samvad.dash.student.load_btn', '→ लोड करें')}
          </button>
        </div>
        {!activeId && (
          <p className="svd-id-hint">
            {tl('samvad.dash.student.not_registered', 'पंजीकृत नहीं हैं?')}{' '}
            <button className="svd-link-btn" onClick={() => onNavigate('/samvad/onboarding')}>
              {tl('samvad.dash.student.register_now', 'अभी पंजीकरण करें →')}
            </button>
          </p>
        )}
      </div>
      )}

      {/* Firebase-linked identity badge */}
      {user && firebaseResolved && activeId && (
        <div className="svd-firebase-badge">
          🔗 {user.displayName ?? user.email ?? 'खाता जुड़ा हुआ'} · <code>{activeId}</code>
        </div>
      )}

      {/* Sign-in nudge for unauthenticated users with no saved ID */}
      {!authLoading && firebaseResolved && !user && !activeId && (
        <div className="svd-signin-nudge">
          <span>💡</span>
          <span>
            Google/X से साइन इन करने पर ID स्वतः लोड होगी।{' '}
            <em>Sign in to auto-load your participant ID.</em>
          </span>
        </div>
      )}

      {error && (
        <div className="svd-error-box">
          ⚠ {error}
          {(error.includes('not found') || error.includes('नहीं मिली')) && (
            <span> — <button className="svd-link-btn" style={{ marginLeft: '0.5rem' }}
              onClick={() => onNavigate('/samvad/onboarding')}>
              🔁 पुनः पंजीकरण करें →
            </button></span>
          )}
        </div>
      )}

      {progress && (
        <>
          {/* Stats row */}
          <div className="svd-stats-grid">
            <div className="svd-stat-card">
              <span className="svd-stat-num">{progress.total_hours.toFixed(1)}</span>
              <span className="svd-stat-lbl">{tl('samvad.dash.student.hours', 'घण्टे · Hours')}</span>
            </div>
            <div className="svd-stat-card">
              <span className="svd-stat-num">{progress.sessions_count}</span>
              <span className="svd-stat-lbl">{tl('samvad.dash.student.sessions', 'सत्र · Sessions')}</span>
            </div>
            <div className="svd-stat-card">
              <span className="svd-stat-num">{progress.books_covered.length}</span>
              <span className="svd-stat-lbl">{tl('samvad.dash.student.books', 'पुस्तकें · Books')}</span>
            </div>
          </div>

          {/* Hours progress bar */}
          <div className="svd-progress-section">
            <div className="svd-progress-header">
              <span className="svd-progress-title">{tl('samvad.dash.student.progress_title', 'घण्टे की प्रगति · Hours Progress')}</span>
              {progress.next_milestone_label && (
                <span className="svd-next-badge">
                  {tl('samvad.dash.student.next', 'अगला')}: {progress.next_milestone_label} ({progress.next_milestone_hours}h)
                </span>
              )}
              {!progress.next_milestone_label && (
                <span className="svd-next-badge svd-next-complete">{tl('samvad.dash.student.all_milestones', '🌟 सभी मील-पत्थर प्राप्त!')}</span>
              )}
            </div>
            <div className="svd-bar-wrap">
              <div className="svd-bar-outer">
                <div className="svd-bar-inner" style={{ width: `${barPct}%` }} />
                {MILESTONES.map(m => (
                  <div
                    key={m.id}
                    className={`svd-bar-tick ${progress.milestone_badges.includes(m.id) ? 'earned' : ''}`}
                    style={{ left: `${m.hours}%` }}
                    title={`${m.labelHi} — ${m.hours}h`}
                  />
                ))}
              </div>
              <span className="svd-bar-end">100h</span>
            </div>
          </div>

          {/* Milestone badges */}
          <div className="svd-section">
            <h3 className="svd-section-title">{tl('samvad.dash.student.milestones_heading', 'उपलब्धि चिह्न · Milestones')}</h3>
            <div className="svd-badge-shelf">
              {MILESTONES.map(m => {
                const earned = progress.milestone_badges.includes(m.id);
                return (
                  <div key={m.id} className={`svd-badge ${earned ? 'earned' : 'locked'}`}>
                    <span className="svd-badge-icon">{earned ? '🏅' : '🔒'}</span>
                    <span className="svd-badge-hi">{m.labelHi}</span>
                    <span className="svd-badge-en">{m.labelEn}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Recent sessions */}
          <div className="svd-section">
            <h3 className="svd-section-title">{tl('samvad.dash.student.recent_sessions', 'हालिया सत्र · Recent Sessions')}</h3>
            {sessions.length > 0 ? (
              <div className="svd-sessions-feed">
                {sessions.map(s => (
                  <div key={s.id} className="svd-session-card">
                    <div className="svd-session-meta">
                      <span className="svd-session-date">{s.date}</span>
                      <span className="svd-session-type">
                        {s.session_type === 'paired' ? tl('samvad.dash.student.paired', '👥 युगल') : tl('samvad.dash.student.solo', '👤 एकल')}
                      </span>
                      <span className="svd-session-hrs">{s.hours.toFixed(2)}h</span>
                      {s.partner_id && (
                        <span className="svd-session-partner">{tl('samvad.dash.student.partner', 'साथी')}: {s.partner_id}</span>
                      )}
                    </div>
                    <p className="svd-session-reflection">"{s.reflection}"</p>
                    {s.youtube_url && (
                      <a
                        className="svd-session-yt"
                        href={s.youtube_url}
                        target="_blank"
                        rel="noopener noreferrer"
                      >
                        {tl('samvad.dash.student.yt_recording', '▶ YouTube रिकॉर्डिंग')}
                      </a>
                    )}
                  </div>
                ))}
              </div>
            ) : (
              <div className="svd-empty-card">
                <p>{tl('samvad.dash.student.no_sessions', 'अभी कोई सत्र लॉग नहीं हुआ।')}</p>
                <p className="svd-muted">{tl('samvad.dash.student.no_sessions_en', 'No sessions logged yet — log your first conversation below.')}</p>
              </div>
            )}
          </div>
        </>
      )}

      {/* Partner matching — only shown after participant record is confirmed valid */}
      {activeId && progress && <SamvadMatchingCard participantId={activeId} onNavigate={onNavigate} />}

      <EventsSection />

      {/* CTAs — always visible */}
      <div className="svd-cta-row">
        <button className="svd-btn-primary" onClick={() => onNavigate('/samvad/session-log')}>
          {tl('samvad.dash.cta.log_session', '📝 सत्र लॉग करें')}
        </button>
        <button className="svd-btn-secondary" onClick={() => onNavigate('/products')}>
          {tl('samvad.dash.cta.view_books', '📚 पुस्तकें देखें')}
        </button>
        {!activeId && (
          <button className="svd-btn-secondary" onClick={() => onNavigate('/samvad/onboarding')}>
            {tl('samvad.dash.cta.register', '📋 पंजीकरण करें')}
          </button>
        )}
      </div>
    </div>
  );
}

// ── Upadhyaya Panel ───────────────────────────────────────────────────────────

function UpadhyayaPanel({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };
  return (
    <div className="svd-panel">
      <div className="svd-role-intro">
        <h2 className="svd-role-title">{tl('samvad.dash.upadhyaya.title', '🎓 उपाध्याय कार्यक्षेत्र')}</h2>
        <p className="svd-role-sub">
          {tl('samvad.dash.upadhyaya.subtitle', 'Upadhyaya — technique transmitter · group ≤ 5 · Adhikara ≥ 6 (primary archetype only)')}
        </p>
      </div>

      <div className="svd-placeholder-grid">
        <div className="svd-placeholder-card svd-ph-active">
          <div className="svd-ph-icon">👥</div>
          <h4>{tl('samvad.dash.upadhyaya.students_heading', 'आपके छात्र · Your Students')}</h4>
          <p>{tl('samvad.dash.upadhyaya.students_body', 'छात्र अपने डैशबोर्ड से आपसे युगलन अनुरोध भेज सकते हैं।')}</p>
          <p className="svd-muted">{tl('samvad.dash.upadhyaya.students_body_en', "Students send partner requests from their dashboard. You'll receive them in your Student panel when logged in as a student participant.")}</p>
          <div className="svd-ph-badge">✅ Sprint 4 Live</div>
        </div>

        <div className="svd-placeholder-card">
          <div className="svd-ph-icon">📋</div>
          <h4>{tl('samvad.dash.upadhyaya.feedback_heading', 'प्रतीक्षित प्रतिक्रिया · Pending Feedback')}</h4>
          <p>{tl('samvad.dash.upadhyaya.feedback_body', 'छात्र प्रतिक्रिया अनुरोध यहाँ दिखेंगे।')}</p>
          <p className="svd-muted">Student feedback requests will appear here once matching is active.</p>
          <div className="svd-ph-badge">🔜 Sprint 4</div>
        </div>

        <div className="svd-placeholder-card">
          <div className="svd-ph-icon">📅</div>
          <h4>{tl('samvad.dash.upadhyaya.workshop_heading', 'कार्यशाला अनुसूची · Workshop Schedule')}</h4>
          <p>{tl('samvad.dash.upadhyaya.workshop_body', 'मासिक कार्यशाला की सूचना portal_config.yaml से लोड होगी।')}</p>
          <p className="svd-muted">Monthly workshop schedule loaded from portal config in Sprint 6.</p>
          <div className="svd-ph-badge">🔜 Sprint 6</div>
        </div>
      </div>

      {/* My Book Drafts — live */}
      <UpadhyayaBookSection onNavigate={onNavigate} />

      <div className="svd-role-tasks">
        <h3 className="svd-section-title">{tl('samvad.dash.phase_activities', 'आपके चरण-कार्य · Your Phase Activities')}</h3>
        <div className="svd-task-list">
          {[
            { phase: '॥ २ ॥', task: 'पाठ-योजना तैयार करें', status: 'active', label: 'Curriculum Planning' },
            { phase: '॥ ३ ॥', task: 'छात्र युगलन की पुष्टि', status: 'pending', label: 'Confirm student pairs' },
            { phase: '॥ ४ ॥', task: 'सत्र का संचालन देखें', status: 'pending', label: 'Observe live sessions' },
            { phase: '॥ ५ ॥', task: 'व्याकरण-अभ्यास का मार्गदर्शन', status: 'pending', label: 'Guide grammar practice' },
            { phase: '॥ ६ ॥', task: 'मूल्यांकन में सहायता', status: 'pending', label: 'Assist in assessments' },
          ].map(task => (
            <div key={task.task} className={`svd-task-row ${task.status}`}>
              <span className="svd-task-phase">{task.phase}</span>
              <span className="svd-task-text">{task.task}</span>
              <span className="svd-task-en">{task.label}</span>
              <span className={`svd-task-status ${task.status}`}>
                {task.status === 'active' ? tl('samvad.dash.task.active', '● सक्रिय') : tl('samvad.dash.task.pending', '○ प्रतीक्षा')}
              </span>
            </div>
          ))}
        </div>
      </div>

      <EventsSection />

      <div className="svd-cta-row">
        <button className="svd-btn-primary" onClick={() => onNavigate('/samvad/content-editor')}>
          {tl('samvad.dash.upadhyaya.cta_new_book', '📖 नई पुस्तक लिखें')}
        </button>
        <button className="svd-btn-secondary" onClick={() => onNavigate('/samvad/session-log')}>
          {tl('samvad.dash.cta.log_session', '📝 सत्र लॉग करें')}
        </button>
        <button className="svd-btn-secondary" onClick={() => onNavigate('/products')}>
          {tl('samvad.dash.cta.view_books', '📚 पुस्तकें देखें')}
        </button>
      </div>
    </div>
  );
}

// ── Acharya Panel ─────────────────────────────────────────────────────────────

function AcharyaPanel({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };
  const [adminData, setAdminData] = useState<AdminData | null>(null);
  const [loading, setLoading]    = useState(false);
  const [error, setError]        = useState('');

  useEffect(() => {
    setLoading(true);
    fetch('/api/samvad/participants')
      .then(r => r.ok ? r.json() as Promise<AdminData> : Promise.reject(r))
      .then(data => { setAdminData(data); })
      .catch(() => setError(tl('samvad.dash.acharya.load_error', 'प्रतिभागी डेटा लोड नहीं हो सका।')))
      .finally(() => setLoading(false));
  }, []);

  const nearAssessment = adminData
    ? adminData.participants.filter(p => p.total_hours >= 20 && p.total_hours < 50)
    : [];

  return (
    <div className="svd-panel">
      <div className="svd-role-intro">
        <h2 className="svd-role-title">{tl('samvad.dash.acharya.title', '🏛️ आचार्य कार्यक्षेत्र')}</h2>
        <p className="svd-role-sub">
          {tl('samvad.dash.acharya.subtitle', 'Acharya — knowledge + character transmitter · group ≤ 9 · 7 Matsya Purāṇa criteria required')}
        </p>
      </div>

      {error && <div className="svd-error-box">⚠ {error}</div>}

      {/* Summary stats */}
      {adminData && (
        <div className="svd-stats-grid">
          <div className="svd-stat-card svd-stat-accent">
            <span className="svd-stat-num">{adminData.total}</span>
            <span className="svd-stat-lbl">{tl('samvad.dash.acharya.stat_participants', 'प्रतिभागी · Participants')}</span>
          </div>
          <div className="svd-stat-card svd-stat-accent">
            <span className="svd-stat-num">{adminData.total_hours_all.toFixed(0)}</span>
            <span className="svd-stat-lbl">{tl('samvad.dash.acharya.stat_hours', 'कुल घण्टे · Total Hours')}</span>
          </div>
          <div className="svd-stat-card svd-stat-accent">
            <span className="svd-stat-num">{nearAssessment.length}</span>
            <span className="svd-stat-lbl">{tl('samvad.dash.acharya.stat_near', 'मूल्यांकन के निकट · Near Assessment')}</span>
          </div>
        </div>
      )}
      {loading && <div className="svd-loading">{tl('samvad.dash.loading', 'लोड हो रहा है…')}</div>}

      <div className="svd-placeholder-grid">
        <div className="svd-placeholder-card svd-ph-active">
          <div className="svd-ph-icon">🎓</div>
          <h4>{tl('samvad.dash.acharya.elevation_heading', 'वरिष्ठ शिष्य उन्नयन · Senior Elevation')}</h4>
          <p>
            {tl('samvad.dash.acharya.elevation_body', 'वरिष्ठ शिष्य उन्नयन के अनुरोध — आचार्य + गुरु की संयुक्त स्वीकृति आवश्यक।')}
          </p>
          <p className="svd-muted">
            {tl('samvad.dash.acharya.elevation_body_en', 'Senior student elevation requests. Requires Acharya + Guru joint confirmation. Never automated.')}
          </p>
          <div className="svd-ph-badge">✅ Sprint 6 Live</div>
        </div>
      </div>

      {/* Live: Book review queue */}
      <AcharyaBookQueue />

      {/* Live: Certification nominations */}
      <AcharyaCertSection />

      {/* Students near assessment */}
      {nearAssessment.length > 0 && (
        <div className="svd-section">
          <h3 className="svd-section-title">{tl('samvad.dash.acharya.near_assessment_heading', 'मूल्यांकन के निकट · Near Assessment (20–50h)')}</h3>
          <div className="svd-mini-roster">
            {nearAssessment.slice(0, 6).map(p => (
              <div key={p.id} className="svd-mini-row">
                <span className="svd-mini-id">{p.id}</span>
                <span className="svd-mini-name">{p.full_name}</span>
                <span className="svd-mini-city">{p.city}</span>
                <span className="svd-mini-hrs">{p.total_hours.toFixed(1)}h</span>
                <span className="svd-mini-lvl">
                  {PROFICIENCY_LABELS[p.proficiency]?.hi ?? `${tl('samvad.dash.level', 'स्तर')} ${p.proficiency}`}
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="svd-role-tasks">
        <h3 className="svd-section-title">{tl('samvad.dash.phase_activities', 'आपके चरण-कार्य · Your Phase Activities')}</h3>
        <div className="svd-task-list">
          {[
            { phase: '॥ २ ॥', task: 'पुस्तक एवं पाठ्यक्रम अनुमोदन', status: 'active', label: 'Book & curriculum approval' },
            { phase: '॥ ५ ॥', task: 'व्याकरण-निरुक्ति परीक्षण', status: 'active', label: 'Grammar-etymology testing' },
            { phase: '॥ ६ ॥', task: 'मौखिक मूल्यांकन का आयोजन', status: 'active', label: 'Oral assessment scheduling' },
            { phase: '॥ ७ ॥', task: 'उपाधि निर्धारण — गुरु के साथ', status: 'pending', label: 'Title granting with Guru' },
            { phase: '॥ ८ ॥', task: 'वरिष्ठ शिष्य उन्नयन की समीक्षा', status: 'pending', label: 'Senior student elevation review' },
          ].map(task => (
            <div key={task.task} className={`svd-task-row ${task.status}`}>
              <span className="svd-task-phase">{task.phase}</span>
              <span className="svd-task-text">{task.task}</span>
              <span className="svd-task-en">{task.label}</span>
              <span className={`svd-task-status ${task.status}`}>
                {task.status === 'active' ? tl('samvad.dash.task.active', '● सक्रिय') : tl('samvad.dash.task.pending', '○ प्रतीक्षा')}
              </span>
            </div>
          ))}
        </div>
      </div>

      <EventsSection />

      <div className="svd-cta-row">
        <button className="svd-btn-secondary" onClick={() => onNavigate('/products')}>
          {tl('samvad.dash.cta.view_books', '📚 पुस्तकें देखें')}
        </button>
      </div>
    </div>
  );
}

// ── Admin Panel ───────────────────────────────────────────────────────────────

type SortField = 'registered_at' | 'total_hours' | 'sessions_count' | 'proficiency';

function AdminPanel({ onNavigate }: { onNavigate: (path: string) => void }) {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };
  const [data, setData]         = useState<AdminData | null>(null);
  const [loading, setLoading]   = useState(false);
  const [error, setError]       = useState('');
  const [sortField, setSortField] = useState<SortField>('total_hours');
  const [sortDesc, setSortDesc] = useState(true);
  const [filter, setFilter]     = useState('');

  useEffect(() => {
    setLoading(true);
    fetch('/api/samvad/participants')
      .then(r => r.ok ? r.json() as Promise<AdminData> : Promise.reject(r))
      .then(d => setData(d))
      .catch(() => setError(tl('samvad.dash.admin.load_error', 'डेटा लोड नहीं हो सका।')))
      .finally(() => setLoading(false));
  }, []);

  function toggleSort(field: SortField) {
    if (sortField === field) setSortDesc(d => !d);
    else { setSortField(field); setSortDesc(true); }
  }

  const sorted = data
    ? [...data.participants]
        .filter(p =>
          !filter ||
          p.full_name.toLowerCase().includes(filter.toLowerCase()) ||
          p.city.toLowerCase().includes(filter.toLowerCase()) ||
          p.id.toLowerCase().includes(filter.toLowerCase())
        )
        .sort((a, b) => {
          const va = a[sortField] as number | string;
          const vb = b[sortField] as number | string;
          return sortDesc
            ? (va < vb ? 1 : va > vb ? -1 : 0)
            : (va < vb ? -1 : va > vb ? 1 : 0);
        })
    : [];

  const SortBtn = ({ field, label }: { field: SortField; label: string }) => (
    <button className={`svd-sort-btn ${sortField === field ? 'active' : ''}`} onClick={() => toggleSort(field)}>
      {label} {sortField === field ? (sortDesc ? '↓' : '↑') : ''}
    </button>
  );

  return (
    <div className="svd-panel">
      <div className="svd-role-intro">
        <h2 className="svd-role-title">{tl('samvad.dash.admin.title', '⚙️ व्यवस्थापक कार्यक्षेत्र')}</h2>
        <p className="svd-role-sub">{tl('samvad.dash.admin.subtitle', 'Admin dashboard — participant roster, hours summary, program health')}</p>
      </div>

      {error && <div className="svd-error-box">⚠ {error}</div>}
      {loading && <div className="svd-loading">{tl('samvad.dash.loading', 'लोड हो रहा है…')}</div>}

      {data && (
        <>
          {/* Summary stats */}
          <div className="svd-stats-grid svd-stats-4">
            <div className="svd-stat-card svd-stat-accent">
              <span className="svd-stat-num">{data.total}</span>
              <span className="svd-stat-lbl">{tl('samvad.dash.admin.stat_total', 'कुल प्रतिभागी')}</span>
            </div>
            <div className="svd-stat-card svd-stat-accent">
              <span className="svd-stat-num">{data.total_hours_all.toFixed(1)}</span>
              <span className="svd-stat-lbl">{tl('samvad.dash.admin.stat_hours', 'कुल घण्टे')}</span>
            </div>
            <div className="svd-stat-card">
              <span className="svd-stat-num">
                {data.participants.filter(p => p.sessions_count > 0).length}
              </span>
              <span className="svd-stat-lbl">{tl('samvad.dash.admin.stat_active', 'सक्रिय छात्र')}</span>
            </div>
            <div className="svd-stat-card">
              <span className="svd-stat-num">
                {data.participants.filter(p => p.milestone_badges.length > 0).length}
              </span>
              <span className="svd-stat-lbl">{tl('samvad.dash.admin.stat_milestones', 'मील-पत्थर विजेता')}</span>
            </div>
          </div>

          {/* Filter + sort controls */}
          <div className="svd-table-controls">
            <input
              className="svd-filter-input"
              type="text"
              placeholder={tl('samvad.dash.admin.filter_placeholder', 'नाम / शहर / ID से खोजें…')}
              value={filter}
              onChange={e => setFilter(e.target.value)}
            />
            <div className="svd-sort-btns">
              <span className="svd-muted">{tl('samvad.dash.admin.sort_label', 'क्रम:')}</span>
              <SortBtn field="total_hours"    label={tl('samvad.dash.admin.sort_hours', 'घण्टे')} />
              <SortBtn field="sessions_count" label={tl('samvad.dash.admin.sort_sessions', 'सत्र')} />
              <SortBtn field="proficiency"    label={tl('samvad.dash.admin.sort_level', 'स्तर')} />
              <SortBtn field="registered_at"  label={tl('samvad.dash.admin.sort_date', 'तिथि')} />
            </div>
          </div>

          {/* Participant table */}
          {sorted.length > 0 ? (
            <div className="svd-table-wrapper">
              <table className="svd-table">
                <thead>
                  <tr>
                    <th>ID</th>
                    <th>{tl('samvad.dash.admin.col_name', 'नाम')}</th>
                    <th>{tl('samvad.dash.admin.col_city', 'शहर')}</th>
                    <th>{tl('samvad.dash.admin.col_level', 'स्तर')}</th>
                    <th>{tl('samvad.dash.admin.col_hours', 'घण्टे')}</th>
                    <th>{tl('samvad.dash.admin.col_sessions', 'सत्र')}</th>
                    <th>{tl('samvad.dash.admin.col_milestones', 'मील-पत्थर')}</th>
                    <th>{tl('samvad.dash.admin.col_status', 'स्थिति')}</th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(p => (
                    <tr key={p.id} className={p.is_minor ? 'svd-row-minor' : ''}>
                      <td className="svd-td-id">{p.id}</td>
                      <td>
                        {p.full_name}
                        {p.is_minor && <span className="svd-minor-tag" title="Minor — guardian visibility policy">🔒</span>}
                      </td>
                      <td>{p.city}</td>
                      <td>
                        <span className="svd-prof-chip">
                          {PROFICIENCY_LABELS[p.proficiency]?.hi ?? p.proficiency}
                        </span>
                      </td>
                      <td className="svd-td-num">{p.total_hours.toFixed(1)}</td>
                      <td className="svd-td-num">{p.sessions_count}</td>
                      <td>
                        {p.milestone_badges.length > 0
                          ? p.milestone_badges.map(b => (
                              <span key={b} className="svd-badge-chip earned">🏅 {b}</span>
                            ))
                          : <span className="svd-muted">—</span>
                        }
                      </td>
                      <td>
                        <span className={`svd-status-chip ${p.status}`}>{p.status}</span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="svd-empty-card">
              <p>{tl('samvad.dash.admin.no_results', 'कोई प्रतिभागी नहीं मिला।')}</p>
              <p className="svd-muted">
                {filter ? tl('samvad.dash.admin.no_results_en', 'Search returned no results.') : tl('samvad.dash.admin.no_participants', 'No participants registered yet.')}
              </p>
            </div>
          )}
        </>
      )}

      <EventsSection />

      <div className="svd-cta-row">
        <button className="svd-btn-secondary" onClick={() => onNavigate('/samvad/onboarding')}>
          {tl('samvad.dash.admin.cta_register', '📋 नया पंजीकरण')}
        </button>
        <button className="svd-btn-secondary" onClick={() => onNavigate('/products')}>
          {tl('samvad.dash.cta.books_short', '📚 पुस्तकें')}
        </button>
      </div>
    </div>
  );
}

// ── Root component ────────────────────────────────────────────────────────────

export default function SamvadDashboard({ onNavigate }: Props) {
  const { t } = useGlossary();
  const tl = (key: string, fallback: string) => { const v = t(key); return v === key ? fallback : v; };
  const { user, authLoading } = useAuth();
  const [showLogin, setShowLogin] = useState(false);
  const [role, setRole] = useState<DashRole>(
    () => (localStorage.getItem('samvad_demo_role') as DashRole | null) ?? 'student'
  );

  function handleRoleChange(r: DashRole) {
    setRole(r);
    localStorage.setItem('samvad_demo_role', r);
  }

  return (
    <div className="svd-root">
      <div className="svd-hero">
        <button className="svd-back-btn" onClick={() => onNavigate('/samvad')}>
          {tl('samvad.dash.back', '← कार्यक्रम पृष्ठ')}
        </button>
        <h1 className="svd-hero-title">{tl('samvad.dash.hero_title', 'संस्कृत युवा संवाद — डैशबोर्ड')}</h1>
        <p className="svd-hero-sub">{tl('samvad.dash.hero_sub', 'Sanskrit Youth Conversation Program · Dashboard')}</p>

        {/* Firebase identity strip */}
        {!authLoading && user && (
          <div className="svd-auth-strip svd-auth-strip--in">
            <span className="svd-auth-avatar">
              {user.photoURL
                ? <img src={user.photoURL} alt="" className="svd-auth-photo" />
                : <span className="svd-auth-initials">{(user.displayName ?? user.email ?? '?')[0].toUpperCase()}</span>
              }
            </span>
            <span className="svd-auth-name">{user.displayName ?? user.email}</span>
            <span className="svd-auth-pill">🔗 Firebase</span>
          </div>
        )}
        {!authLoading && !user && (
          <div className="svd-auth-strip svd-auth-strip--out">
            <span className="svd-auth-hint">
              {tl('samvad.dash.signin_hint', 'Google/X से साइन इन करें — ID स्वतः लोड होगी।')}
            </span>
            <button className="svd-auth-signin-btn" onClick={() => setShowLogin(true)}>
              🔑 {tl('samvad.dash.signin_btn', 'साइन इन करें')}
            </button>
          </div>
        )}
      </div>

      {showLogin && <LoginModal onClose={() => setShowLogin(false)} />}

      {/* Role switcher */}
      <div className="svd-role-strip">
        <span className="svd-role-strip-label">{tl('samvad.dash.role_label', 'भूमिका · Role')}</span>
        {(Object.entries(ROLE_META) as [DashRole, typeof ROLE_META[DashRole]][]).map(([key, meta]) => (
          <button
            key={key}
            className={`svd-role-pill ${role === key ? 'active' : ''}`}
            onClick={() => handleRoleChange(key)}
          >
            {meta.icon} {meta.label}
            <span className="svd-role-pill-en">{meta.labelEn}</span>
          </button>
        ))}
        {/* Only show Demo badge when user is NOT signed in */}
        {!user && (
          <span className="svd-demo-badge" title="Sign in with Google or X to link your participant ID automatically">
            ⚠ Demo
          </span>
        )}
      </div>

      {/* Role panels */}
      {role === 'student'   && <StudentPanel   onNavigate={onNavigate} />}
      {role === 'upadhyaya' && <UpadhyayaPanel onNavigate={onNavigate} />}
      {role === 'acharya'   && <AcharyaPanel   onNavigate={onNavigate} />}
      {role === 'admin'     && <AdminPanel     onNavigate={onNavigate} />}
    </div>
  );
}
